import { useEffect, useState } from 'react';
import { useAuthStore } from '../../../auth/authStore';
import { useTranslation } from '../../../i18n/useTranslation';
import { fetchAdminItems, createAdminItem, updateAdminItem } from '../../../api/adminItemApi';
import { Item } from '../../../types/interfaces';
import { useToast, ToastContainer } from '../../../components/Toast/Toast';
import styles from './AdminItemPage.module.css';

export function AdminItemPage() {
  const { token } = useAuthStore();
  const { t } = useTranslation();
  const [items, setItems] = useState<Item[]>([]);
  const [monsters, setMonsters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState<Item | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [search, setSearch] = useState('');

  const [formData, setFormData] = useState<Partial<Item>>({});
  const [uploading, setUploading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const { messages, toast, remove } = useToast();

  useEffect(() => {
    if (token) {
      loadItems();
      loadMonsters();
    }
  }, [token]);

  const loadMonsters = async () => {
    try {
      const { monsterApi } = await import('../../../api/adminApi');
      const data = await monsterApi.list();
      setMonsters(data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchAdminItems(token!);
      setItems(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: Item) => {
    setIsEditing(item);
    setIsCreating(false);
    setFormData({ ...item });
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setIsEditing(null);
    setFormData({ slot: 'Head', min_level: 1, width: 1, height: 1, is_active: true, bonuses: [], item_type: 'equipment', max_durability: 0, temporary_effects: [], buy_price: 0, sell_price: 0, merchant_stock: false, merchant_mode: 'none', merchant_qty: 0, merchant_max: 0, requires_forge: false, forge_recipe: [], forge_cost: 0, forge_energy_cost: 0, forge_min_xp: 0, forge_time: 0, energyRestorePercentage: null, energyRegenBoostPercentage: null, boostDuration: null, hpRestorePercentage: null });
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        await createAdminItem(token!, formData);
        toast.success(t('admin.items.modal.new') + ' - ' + t('common.saved'));
      } else if (isEditing) {
        // Estoque muda sozinho com compras/vendas dos jogadores: só envia
        // merchant_qty se o admin alterou o valor — senão, salvar outro campo
        // sobrescreveria o estoque real com o número antigo do formulário.
        const payload = { ...formData };
        if (payload.merchant_qty === isEditing.merchant_qty) delete payload.merchant_qty;
        await updateAdminItem(token!, isEditing.id, payload);
        toast.success(t('admin.items.modal.edit') + ' - ' + t('common.saved'));
      }
      setIsEditing(null);
      setIsCreating(false);
      loadItems();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'image/png') {
      toast.error(t('admin.items.modal.onlyPng'));
      return;
    }
    try {
      setUploading(true);
      const form = new FormData();
      form.append('icon', file);
      const res = await fetch('/api/admin/upload/item-icon', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      // Resposta pode não ser JSON (413 do proxy, página de erro do PHP) —
      // ler como texto evita o "Unexpected token '<'" e mostra o motivo real.
      const raw = await res.text();
      let data: any = null;
      try { data = JSON.parse(raw); } catch { /* não-JSON */ }
      if (!res.ok || !data) {
        if (res.status === 413) throw new Error('Arquivo grande demais para o servidor (413).');
        throw new Error(data?.error ?? `Falha no upload (HTTP ${res.status}).`);
      }
      setFormData(prev => ({ ...prev, icon_url: data.icon_url }));
      toast.success("Ícone enviado com sucesso");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleToggleActive = async (item: Item) => {
    try {
      await updateAdminItem(token!, item.id, { is_active: !item.is_active });
      loadItems();
      toast.success(item.is_active ? t('admin.items.btn.deactivate') : t('admin.items.btn.activate'));
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  if (loading) return <div className={styles.container}>{t('common.loading')}</div>;

  // Pesquisa: nome (traduzido e cru), slot, tipo e id-base do ícone.
  const q = search.trim().toLowerCase();
  const visibleItems = q
    ? items.filter(it => {
        const baseId = it.icon_url
          ? it.icon_url.split('/').pop()?.replace('.png', '')
          : it.name.toLowerCase().replace(/\s+/g, '_');
        const tn = t(`item.${baseId}.name` as any) || it.name;
        return [it.name, tn, it.slot, it.item_type, baseId]
          .some(v => (v ?? '').toString().toLowerCase().includes(q));
      })
    : items;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <h2>{t('admin.items.title')}</h2>
          <button className={styles.btnGuide} onClick={() => setShowGuide(true)}>
            📖 {t('guide.items.btn' as any)}
          </button>
        </div>
        <button className={styles.btnCreate} onClick={handleCreateNew}>
          ✨ {t('admin.items.new')}
        </button>
      </header>

      {error && <div className={styles.error}>{error}</div>}

      <div style={{ position: 'relative', margin: '0 0 12px', maxWidth: 420 }}>
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.5, pointerEvents: 'none' }}>🔍</span>
        <input
          className="formInput"
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('admin.items.searchPlaceholder' as any) || 'Pesquisar item por nome, slot ou tipo…'}
          style={{ width: '100%', paddingLeft: 32 }}
        />
        {q && (
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 11, opacity: 0.6 }}>
            {visibleItems.length}/{items.length}
          </span>
        )}
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('admin.items.col.icon')}</th>
              <th>{t('admin.items.col.name')}</th>
              <th>{t('admin.items.col.slot')}</th>
              <th>{t('admin.items.col.type')}</th>
              <th>{t('admin.items.col.level')}</th>
              <th>{t('admin.items.col.durability')}</th>
              <th>{t('admin.items.col.status')}</th>
              <th>{t('admin.items.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map(item => {
              // Obter o ID limpo (sem extensão) a partir da imagem, ou usar o nome normalizado se não tiver imagem
              const baseId = item.icon_url 
                  ? item.icon_url.split('/').pop()?.replace('.png', '') 
                  : item.name.toLowerCase().replace(/\s+/g, '_');
                  
              const nameKey = `item.${baseId}.name` as any;
              const translatedName = t(nameKey) || item.name;

              return (
                <tr key={item.id} className={!item.is_active ? styles.inactive : ''}>
                  <td>
                    {item.icon_url ? (
                      <img src={`${item.icon_url}?v=3`} alt={translatedName} className={styles.icon} />
                    ) : (
                      <div className={styles.iconPlaceholder}>{item.name.charAt(0)}</div>
                    )}
                  </td>
                  <td>{translatedName}</td>
                  <td>{item.slot}</td>
                  <td>
                    <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: item.item_type === 'consumable' ? 'rgba(80,200,120,0.1)' : item.item_type === 'material' ? 'rgba(180,180,180,0.1)' : 'rgba(80,120,200,0.1)', color: item.item_type === 'consumable' ? '#60d080' : item.item_type === 'material' ? '#999' : '#80b0e0' }}>
                      {item.item_type === 'consumable' ? t('admin.items.modal.typeConsumable') : item.item_type === 'material' ? t('admin.items.modal.typeMaterial') : t('admin.items.modal.typeEquipment')}
                    </span>
                    {item.requires_forge && (
                      <span style={{ fontSize: 10, padding: '2px 5px', borderRadius: 4, background: 'rgba(255,136,51,0.12)', color: '#ff8833', fontWeight: 700, marginLeft: 4, border: '1px solid rgba(255,136,51,0.25)' }}>
                        🔨
                      </span>
                    )}
                  </td>
                  <td>{item.min_level}</td>
                  <td>{item.max_durability ? item.max_durability : '∞'}</td>
                  <td>
                    <span className={item.is_active ? styles.statusActive : styles.statusInactive}>
                      {item.is_active ? t('admin.items.status.active') : t('admin.items.status.inactive')}
                    </span>
                    {item.merchant_mode === 'stock' && (
                      <span className={styles.merchantBadge} title="Estoque da Mercadora">
                        📦 {item.merchant_qty ?? 0}{item.merchant_max ? ` / ${item.merchant_max}` : ''}
                      </span>
                    )}
                    {item.merchant_mode === 'unlimited' && (
                      <span className={styles.merchantBadge} title="Mercadora vende sem limite">∞ loja</span>
                    )}
                  </td>
                  <td className={styles.actions}>
                    <button onClick={() => handleEdit(item)}>{t('common.edit')}</button>
                    <button onClick={() => handleToggleActive(item)}>
                      {item.is_active ? t('admin.items.btn.deactivate') : t('admin.items.btn.activate')}
                    </button>
                  </td>
                </tr>
              );
            })}
            {visibleItems.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center' }}>
                  {items.length === 0 ? t('admin.items.empty') : (t('admin.items.noMatch' as any) || 'Nenhum item encontrado.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal / Form para Criar e Editar */}
      {(isCreating || isEditing) && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>{isCreating ? t('admin.items.modal.new') : t('admin.items.modal.edit')}</h3>
            <div className={styles.formContainer}>
              {/* Coluna Esquerda: Informações Básicas */}
              <div className={styles.formGroup}>
                <h4>{t('admin.items.modal.basic')}</h4>

                <label>
                  <span>{t('admin.items.col.name')}</span>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Espada de Ferro, Elmo Antigo..."
                  />
                  <small className={styles.fieldHint}>Nome que aparece no inventário e loja do jogo.</small>
                </label>

                <label>
                  <span>{t('admin.items.col.slot')}</span>
                  <select
                    value={formData.slot || 'Head'}
                    onChange={e => {
                      const newSlot = e.target.value;
                      // Infere o tipo automaticamente pelo slot
                      let inferredType = formData.item_type || 'equipment';
                      if (newSlot === 'Consumable') inferredType = 'consumable';
                      else if (newSlot === 'Material') inferredType = 'material';
                      else inferredType = 'equipment';
                      setFormData({ ...formData, slot: newSlot, item_type: inferredType });
                    }}
                  >
                    {['Head', 'Body', 'Legs', 'Right_Hand', 'Left_Hand', 'Consumable', 'Material'].map(s => {
                      let label = s;
                      if (s === 'Head') label = t('equipment.slot.head' as any);
                      if (s === 'Body') label = t('equipment.slot.body' as any);
                      if (s === 'Legs') label = t('equipment.slot.legs' as any);
                      if (s === 'Right_Hand') label = t('equipment.slot.rightHand' as any);
                      if (s === 'Left_Hand') label = t('equipment.slot.leftHand' as any);
                      if (s === 'Consumable') label = t('equipment.consumable' as any);
                      if (s === 'Material') label = t('admin.items.modal.typeMaterial' as any);
                      return <option key={s} value={s}>{label}</option>;
                    })}
                  </select>
                  <small className={styles.fieldHint}>
                    {t(`admin.items.slotHint.${formData.slot || 'Head'}` as any) || t('admin.items.slotHint.default' as any)}
                  </small>
                </label>

                {/* Tipo do item */}
                <label>
                  <span>{t('admin.items.modal.itemType')}</span>
                  <select
                    value={formData.item_type || 'equipment'}
                    onChange={e => setFormData({ ...formData, item_type: e.target.value as any })}
                  >
                    <option value="equipment">{t('admin.items.modal.typeEquipment')}</option>
                    <option value="consumable">{t('admin.items.modal.typeConsumable')}</option>
                    <option value="material">{t('admin.items.modal.typeMaterial')}</option>
                  </select>
                  <small className={styles.fieldHint}>Define a lógica de uso: equipar, consumir ou material de craft.</small>
                </label>

                {/* Durabilidade máxima (apenas para equipamentos) */}
                {(formData.item_type === 'equipment' || !formData.item_type) && (
                  <label>
                    <span>{t('admin.items.modal.durability')}</span>
                    <input
                      type="number"
                      min="0"
                      value={formData.max_durability ?? 0}
                      onChange={e => setFormData({ ...formData, max_durability: parseInt(e.target.value) || 0 })}
                    />
                    <small className={styles.fieldHint}>{t('admin.items.modal.durabilityHint')}</small>
                  </label>
                )}

                <label>
                  <span>{t('admin.items.col.level')}</span>
                  <input
                    type="number"
                    min="1"
                    value={formData.min_level || 1}
                    onChange={e => setFormData({ ...formData, min_level: parseInt(e.target.value) || 1 })}
                  />
                  <small className={styles.fieldHint}>Nível mínimo do personagem para equipar este item.</small>
                </label>

                <div className={styles.fieldRow}>
                  <label>
                    <span>Largura (Grid)</span>
                    <input
                      type="number"
                      min="1"
                      max="4"
                      value={formData.width || 1}
                      onChange={e => setFormData({ ...formData, width: parseInt(e.target.value) || 1 })}
                    />
                    <small className={styles.fieldHint}>Colunas no inventário (1–4).</small>
                  </label>
                  <label>
                    <span>Altura (Grid)</span>
                    <input
                      type="number"
                      min="1"
                      max="4"
                      value={formData.height || 1}
                      onChange={e => setFormData({ ...formData, height: parseInt(e.target.value) || 1 })}
                    />
                    <small className={styles.fieldHint}>Linhas no inventário (1–4).</small>
                  </label>
                </div>

                {/* ── Mercado / Economia ── */}
                <div className={styles.fieldRow}>
                  <label>
                    <span>💰 Preço de compra (Mercador)</span>
                    <input
                      type="number"
                      min="0"
                      value={formData.buy_price ?? 0}
                      onChange={e => setFormData({ ...formData, buy_price: parseInt(e.target.value) || 0 })}
                    />
                    <small className={styles.fieldHint}>Quanto o jogador paga em Ta Feito. 0 = não vendido.</small>
                  </label>
                  <label>
                    <span>🪙 Preço de venda (à Mercadora)</span>
                    <input
                      type="number"
                      min="0"
                      value={formData.sell_price ?? 0}
                      onChange={e => setFormData({ ...formData, sell_price: parseInt(e.target.value) || 0 })}
                    />
                    <small className={styles.fieldHint}>Quanto o jogador recebe por unidade. 0 = não pode vender.</small>
                  </label>
                </div>
                {/* ── Estoque da Mercadora ── */}
                {(() => {
                  const mode = formData.merchant_mode ?? (formData.merchant_stock ? 'unlimited' : 'none');
                  const setMode = (m: 'none' | 'unlimited' | 'stock') =>
                    setFormData({ ...formData, merchant_mode: m, merchant_stock: m !== 'none' });
                  const MODES: Array<{ id: 'none' | 'unlimited' | 'stock'; icon: string; title: string; desc: string }> = [
                    { id: 'none',      icon: '🚫', title: 'Não vende',
                      desc: 'Não aparece na aba "Comprar". Se tiver preço de venda, a Mercadora ainda compra do jogador, mas o item some (não volta à loja).' },
                    { id: 'unlimited', icon: '∞',  title: 'Ilimitado',
                      desc: 'Sempre à venda, sem quantidade. Vendas dos jogadores não alteram nada.' },
                    { id: 'stock',     icon: '📦', title: 'Por estoque',
                      desc: 'Vende só o que houver em estoque. Cada unidade vendida pelos jogadores entra no estoque e fica à venda; com estoque 0, o item some da loja.' },
                  ];
                  return (
                    <div className={styles.merchantBox}>
                      <span className={styles.merchantBoxTitle}>🛒 Estoque da Mercadora</span>
                      <div className={styles.merchantModes}>
                        {MODES.map(m => (
                          <button
                            key={m.id}
                            type="button"
                            className={`${styles.merchantMode} ${mode === m.id ? styles.merchantModeOn : ''}`}
                            onClick={() => setMode(m.id)}
                          >
                            <strong>{m.icon} {m.title}</strong>
                            <small>{m.desc}</small>
                          </button>
                        ))}
                      </div>

                      {mode !== 'none' && (formData.buy_price ?? 0) <= 0 && (
                        <small className={styles.merchantWarn}>⚠ Defina um preço de compra maior que 0 — sem preço o item não aparece na loja.</small>
                      )}
                      {mode === 'stock' && (formData.sell_price ?? 0) <= 0 && (
                        <small className={styles.merchantWarn}>⚠ Sem preço de venda os jogadores não conseguem vender este item — o estoque só muda pelo valor definido aqui.</small>
                      )}

                      {mode === 'stock' && (
                        <div className={styles.fieldRow}>
                          <label>
                            <span>📦 Estoque atual</span>
                            <input
                              type="number"
                              min="0"
                              value={formData.merchant_qty ?? 0}
                              onChange={e => setFormData({ ...formData, merchant_qty: Math.max(0, parseInt(e.target.value) || 0) })}
                            />
                            <small className={styles.fieldHint}>Unidades disponíveis agora. Sobe quando jogadores vendem e desce nas compras.</small>
                          </label>
                          <label>
                            <span>🔝 Estoque máximo</span>
                            <input
                              type="number"
                              min="0"
                              value={formData.merchant_max ?? 0}
                              onChange={e => setFormData({ ...formData, merchant_max: Math.max(0, parseInt(e.target.value) || 0) })}
                            />
                            <small className={styles.fieldHint}>0 = sem teto. Vendas além do teto pagam o jogador, mas não entram no estoque.</small>
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <label>
                  <span>{t('admin.items.modal.desc')}</span>
                  <textarea
                    value={formData.description || ''}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    placeholder="Descreva o item, sua história ou efeito especial..."
                  />
                  <small className={styles.fieldHint}>Texto exibido ao inspecionar o item no jogo.</small>
                </label>

                {/* Efeitos Temporários (apenas para consumíveis) */}
                {formData.item_type === 'consumable' && (
                  <div style={{ marginTop: 8 }}>
                    <h4 style={{ fontSize: 13, color: '#60d080', marginBottom: 6 }}>{t('admin.items.modal.tempEffects')}</h4>
                    {(formData.temporary_effects || []).map((eff: any, idx: number) => (
                      <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                        <select
                          value={eff.attribute || 'hp_max'}
                          onChange={e => {
                            const arr = [...(formData.temporary_effects || [])] as any[];
                            arr[idx] = { ...arr[idx], attribute: e.target.value };
                            setFormData({ ...formData, temporary_effects: arr });
                          }}
                          style={{ flex: 1, fontSize: 12 }}
                        >
                          {['hp_max','ap_max','mp_max','strength','intelligence','agility','luck','wisdom','initiative','dodge','block'].map(a => (
                            <option key={a} value={a}>{a}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          value={eff.value || 0}
                          onChange={e => {
                            const arr = [...(formData.temporary_effects || [])] as any[];
                            arr[idx] = { ...arr[idx], value: parseInt(e.target.value) || 0 };
                            setFormData({ ...formData, temporary_effects: arr });
                          }}
                          placeholder={t('admin.items.modal.effectValue')}
                          style={{ width: 60, fontSize: 12 }}
                        />
                        <input
                          type="number"
                          value={eff.duration_minutes || 30}
                          onChange={e => {
                            const arr = [...(formData.temporary_effects || [])] as any[];
                            arr[idx] = { ...arr[idx], duration_minutes: parseInt(e.target.value) || 30 };
                            setFormData({ ...formData, temporary_effects: arr });
                          }}
                          placeholder="min"
                          style={{ width: 50, fontSize: 12 }}
                        />
                        <span style={{ fontSize: 10, color: '#5a5040' }}>min</span>
                        <button
                          type="button"
                          onClick={() => {
                            const arr = [...(formData.temporary_effects || [])] as any[];
                            arr.splice(idx, 1);
                            setFormData({ ...formData, temporary_effects: arr });
                          }}
                          style={{ background: 'none', border: 'none', color: '#ff6060', cursor: 'pointer', fontSize: 14 }}
                        >✕</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        temporary_effects: [...(formData.temporary_effects || []), { attribute: 'hp_max', value: 100, duration_minutes: 30 }] as any
                      })}
                      style={{ fontSize: 11, padding: '4px 10px', background: 'rgba(60,200,100,0.1)', border: '1px solid rgba(60,200,100,0.2)', color: '#60d080', borderRadius: 6, cursor: 'pointer' }}
                    >
                      {t('admin.items.modal.addEffect')}
                      {t('admin.items.modal.addEffect')}
                    </button>

                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, padding: 8, background: 'rgba(60,200,100,0.05)', border: '1px solid rgba(60,200,100,0.1)', borderRadius: 6 }}>
                      <h4 style={{ fontSize: 13, color: '#60d080', margin: 0 }}>⚡ Energia</h4>
                      <label className={styles.formGroup} style={{ gap: 5 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#a09070' }}>Restaurar Energia (%)</span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.energyRestorePercentage || ''}
                          onChange={e => setFormData({ ...formData, energyRestorePercentage: parseInt(e.target.value) || null })}
                          style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(200,160,60,0.25)', color: '#e8dcc8', padding: '8px 10px', borderRadius: 6, fontSize: 13 }}
                          placeholder="Ex: 50"
                        />
                        <small className={styles.fieldHint}>Recupera X% da energia total máxima ao consumir.</small>
                      </label>
                      <label className={styles.formGroup} style={{ gap: 5 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#a09070' }}>Boost de Regeneração (%)</span>
                        <input
                          type="number"
                          min="0"
                          value={formData.energyRegenBoostPercentage || ''}
                          onChange={e => setFormData({ ...formData, energyRegenBoostPercentage: parseInt(e.target.value) || null })}
                          style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(200,160,60,0.25)', color: '#e8dcc8', padding: '8px 10px', borderRadius: 6, fontSize: 13 }}
                          placeholder="Ex: 50 (aumenta a vel. em 50%)"
                        />
                      </label>
                      <label className={styles.formGroup} style={{ gap: 5 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#a09070' }}>Duração do Boost (segundos)</span>
                        <input
                          type="number"
                          min="0"
                          value={formData.boostDuration || ''}
                          onChange={e => setFormData({ ...formData, boostDuration: parseInt(e.target.value) || null })}
                          style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(200,160,60,0.25)', color: '#e8dcc8', padding: '8px 10px', borderRadius: 6, fontSize: 13 }}
                          placeholder="Tempo do boost em segundos"
                        />
                      </label>
                    </div>

                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, padding: 8, background: 'rgba(220,60,60,0.05)', border: '1px solid rgba(220,60,60,0.12)', borderRadius: 6 }}>
                      <h4 style={{ fontSize: 13, color: '#ff8080', margin: 0 }}>❤️ Vida</h4>
                      <label className={styles.formGroup} style={{ gap: 5 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#a09070' }}>Restaurar HP (%)</span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.hpRestorePercentage ?? ''}
                          onChange={e => setFormData({ ...formData, hpRestorePercentage: e.target.value === '' ? null : (parseInt(e.target.value) || 0) })}
                          style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(200,160,60,0.25)', color: '#e8dcc8', padding: '8px 10px', borderRadius: 6, fontSize: 13 }}
                          placeholder="Ex: 30"
                        />
                        <small className={styles.fieldHint}>Ao usar UMA unidade, cura X% do HP máximo (na batalha e no Hub). Deixe vazio para não curar por %.</small>
                      </label>
                    </div>
                  </div>
                )}

                {/* ── Toggle: Requer Forja ── */}
                <label style={{ flexDirection: 'row', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.requires_forge || false}
                    onChange={e => setFormData({ ...formData, requires_forge: e.target.checked })}
                    style={{ width: 18, height: 18, accentColor: '#ff8833', cursor: 'pointer' }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: formData.requires_forge ? '#ff8833' : '#a09070' }}>
                      🔨 {t('admin.items.forge.toggle' as any)}
                    </span>
                    <small className={styles.fieldHint}>{t('admin.items.forge.toggleHint' as any)}</small>
                  </div>
                </label>
              </div>


              {/* Coluna Direita: Visual & Drops */}
              <div className={styles.formGroup}>
                <h4>{t('admin.items.modal.visual')}</h4>

                {/* ── Upload PNG ── */}
                <label className={styles.uploadLabel}>
                  <span>{t('admin.items.modal.uploadIcon')}</span>
                  <div className={styles.uploadRow}>
                    <label className={styles.uploadBtn}>
                      {uploading ? '⏳ ' + t('common.loading') : '📁 ' + t('admin.items.modal.chooseFile')}
                      <input
                        type="file"
                        accept="image/png"
                        style={{ display: 'none' }}
                        onChange={handleIconUpload}
                        disabled={uploading}
                      />
                    </label>
                    <span className={styles.uploadHint}>.png — máx. 2 MB</span>
                  </div>
                  <small className={styles.fieldHint}>{t('admin.items.modal.uploadIconHint' as any)}</small>
                </label>

                {/* ── Manual URL fallback ── */}
                <label>
                  <span>{t('admin.items.modal.iconUrl')}</span>
                  <input
                    type="text"
                    value={formData.icon_url || ''}
                    onChange={e => setFormData({ ...formData, icon_url: e.target.value })}
                    placeholder="/sprites/items/my_item.png"
                  />
                  <small className={styles.fieldHint}>{t('admin.items.modal.iconUrlHint' as any)}</small>
                </label>

                {/* Preview */}
                <div className={styles.imagePreview}>
                  {formData.icon_url ? (
                    <img src={`${formData.icon_url}?v=${Date.now()}`} alt="Preview" />
                  ) : (
                    <div className={styles.imagePlaceholder}>{t('admin.items.modal.noIcon')}</div>
                  )}
                </div>

              </div>
            </div>

            <div className={styles.fullWidthSections}>
              {/* Permanent Bonuses (apenas para equipamentos) */}
              {(formData.item_type === 'equipment' || !formData.item_type) && (
                <div className={styles.dropsSection}>
                  <h4>Atributos Bônus (Equipamento)</h4>
                  <div className={styles.dropsList}>
                    {(formData.bonuses || []).length === 0 && (
                      <span style={{ fontSize: 12, color: '#5a5040', fontStyle: 'italic', padding: '4px 0' }}>
                        Nenhum bônus configurado.
                      </span>
                    )}
                    {(formData.bonuses || []).map((bonus: any, idx: number) => (
                      <div key={idx} className={styles.dropRow}>
                        <select
                          value={bonus.attribute || 'hp_max'}
                          onChange={e => {
                            const newBonuses = [...(formData.bonuses || [])] as any[];
                            newBonuses[idx] = { ...newBonuses[idx], attribute: e.target.value };
                            setFormData({ ...formData, bonuses: newBonuses });
                          }}
                        >
                          {['hp_max','ap_max','mp_max','strength','intelligence','agility','luck','wisdom','initiative','dodge','block'].map(a => (
                            <option key={a} value={a}>{a}</option>
                          ))}
                        </select>
                        <div className={styles.dropChanceWrapper}>
                          <select
                            value={bonus.type || 'flat'}
                            onChange={e => {
                              const newBonuses = [...(formData.bonuses || [])] as any[];
                              newBonuses[idx] = { ...newBonuses[idx], type: e.target.value };
                              setFormData({ ...formData, bonuses: newBonuses });
                            }}
                            style={{ width: '80px', marginRight: '4px' }}
                          >
                            <option value="flat">Fixo (+)</option>
                            <option value="percent">Porcent. (%)</option>
                          </select>
                          <input
                            type="number"
                            value={bonus.value || 0}
                            onChange={e => {
                              const newBonuses = [...(formData.bonuses || [])] as any[];
                              newBonuses[idx] = { ...newBonuses[idx], value: parseInt(e.target.value) || 0 };
                              setFormData({ ...formData, bonuses: newBonuses });
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          className={styles.btnRemoveDrop}
                          title="Remover Bônus"
                          onClick={() => {
                            const newBonuses = [...(formData.bonuses || [])];
                            newBonuses.splice(idx, 1);
                            setFormData({ ...formData, bonuses: newBonuses });
                          }}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className={styles.btnAddDrop}
                    onClick={() => setFormData({ ...formData, bonuses: [...(formData.bonuses || []), { attribute: 'hp_max', value: 10, type: 'flat' }] as any })}
                  >
                    + Adicionar Bônus
                  </button>

                  <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, padding: 8, background: 'rgba(60,200,100,0.05)', border: '1px solid rgba(60,200,100,0.1)', borderRadius: 6 }}>
                    <h4 style={{ fontSize: 13, color: '#60d080', margin: 0 }}>⚡ Energia (Bônus Passivo)</h4>
                    <label className={styles.formGroup} style={{ gap: 5 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#a09070' }}>Boost de Regeneração (%)</span>
                      <input
                        type="number"
                        min="0"
                        value={formData.energyRegenBoostPercentage || ''}
                        onChange={e => setFormData({ ...formData, energyRegenBoostPercentage: parseInt(e.target.value) || null })}
                        style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(200,160,60,0.25)', color: '#e8dcc8', padding: '8px 10px', borderRadius: 6, fontSize: 13 }}
                        placeholder="Ex: 50 (aumenta a vel. em 50%)"
                      />
                      <small className={styles.fieldHint}>Aplica o boost de regeneração enquanto o item estiver equipado.</small>
                    </label>
                  </div>
                </div>
              )}

              <div className={styles.dropsSection}>
                <h4>Drops de Monstros</h4>
                <div className={styles.dropsList}>
                  {(formData.drops || []).length === 0 && (
                    <span style={{ fontSize: 12, color: '#5a5040', fontStyle: 'italic', padding: '4px 0' }}>
                      Nenhum drop configurado.
                    </span>
                  )}
                  {(formData.drops || []).map((drop, idx) => (
                    <div key={idx} className={styles.dropRow}>
                      <select
                        value={drop.monster_id}
                        onChange={e => {
                          const newDrops = [...(formData.drops || [])];
                          newDrops[idx].monster_id = e.target.value;
                          setFormData({ ...formData, drops: newDrops });
                        }}
                      >
                        <option value="">Selecione um monstro...</option>
                        {monsters.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                      <div className={styles.dropChanceWrapper}>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={drop.drop_chance}
                          onChange={e => {
                            const newDrops = [...(formData.drops || [])];
                            newDrops[idx].drop_chance = Number(e.target.value);
                            setFormData({ ...formData, drops: newDrops });
                          }}
                        />
                        <span>%</span>
                      </div>
                      <button
                        type="button"
                        className={styles.btnRemoveDrop}
                        title="Remover drop"
                        onClick={() => {
                          const newDrops = [...(formData.drops || [])];
                          newDrops.splice(idx, 1);
                          setFormData({ ...formData, drops: newDrops });
                        }}
                      >✕</button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.btnAddDrop}
                  onClick={() => setFormData({ ...formData, drops: [...(formData.drops || []), { monster_id: '', drop_chance: 10 }] })}
                >
                  + Adicionar Drop
                </button>
              </div>

              {/* ── Forge Configuration (quando requires_forge está ativo) ── */}
              {formData.requires_forge && (
                <div className={styles.dropsSection}>
                  <h4 style={{ color: '#ff8833' }}>🔨 {t('admin.items.forge.section' as any)}</h4>

                  {/* Ingredientes */}
                  <div style={{ marginBottom: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#cc8833', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('admin.items.forge.recipe' as any)}
                    </span>
                    <small className={styles.fieldHint} style={{ display: 'block', marginBottom: 8 }}>
                      {t('admin.items.forge.recipeHint' as any)}
                    </small>

                    <div className={styles.dropsList}>
                      {(formData.forge_recipe || []).length === 0 && (
                        <span style={{ fontSize: 12, color: '#5a5040', fontStyle: 'italic', padding: '4px 0' }}>
                          Nenhum ingrediente configurado.
                        </span>
                      )}
                      {(formData.forge_recipe || []).map((ing: any, idx: number) => (
                        <div key={idx} className={styles.dropRow}>
                          <select
                            value={ing.item_id || ''}
                            onChange={e => {
                              const arr = [...(formData.forge_recipe || [])] as any[];
                              arr[idx] = { ...arr[idx], item_id: e.target.value };
                              setFormData({ ...formData, forge_recipe: arr });
                            }}
                          >
                            <option value="">{t('admin.items.forge.selectItem' as any)}</option>
                            {items
                              .filter(i => i.id !== formData.id) // Não pode ser ingrediente de si mesmo
                              .map(i => (
                                <option key={i.id} value={i.id}>
                                  {i.name} ({i.item_type === 'material' ? 'Material' : i.slot})
                                </option>
                              ))}
                          </select>
                          <div className={styles.dropChanceWrapper}>
                            <span style={{ fontSize: 10, color: '#8a7a60' }}>{t('admin.items.forge.quantity' as any)}</span>
                            <input
                              type="number"
                              min="1"
                              value={ing.quantity || 1}
                              onChange={e => {
                                const arr = [...(formData.forge_recipe || [])] as any[];
                                arr[idx] = { ...arr[idx], quantity: parseInt(e.target.value) || 1 };
                                setFormData({ ...formData, forge_recipe: arr });
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className={styles.btnRemoveDrop}
                            title="Remover ingrediente"
                            onClick={() => {
                              const arr = [...(formData.forge_recipe || [])];
                              arr.splice(idx, 1);
                              setFormData({ ...formData, forge_recipe: arr });
                            }}
                          >✕</button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className={styles.btnAddDrop}
                      style={{ borderColor: 'rgba(255, 136, 51, 0.4)', color: '#ff8833', background: 'rgba(255, 136, 51, 0.08)' }}
                      onClick={() => setFormData({
                        ...formData,
                        forge_recipe: [...(formData.forge_recipe || []), { item_id: '', quantity: 1 }] as any
                      })}
                    >
                      {t('admin.items.forge.addIngredient' as any)}
                    </button>
                  </div>

                  {/* Custo, Nível Mínimo, Tempo */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
                    <label className={styles.formGroup} style={{ gap: 5 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#a09070', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {t('admin.items.forge.cost' as any)}
                      </span>
                      <input
                        type="number"
                        min="0"
                        value={formData.forge_cost ?? 0}
                        onChange={e => setFormData({ ...formData, forge_cost: parseInt(e.target.value) || 0 })}
                        style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(200,160,60,0.25)', color: '#e8dcc8', padding: '8px 10px', borderRadius: 6, fontSize: 13 }}
                      />
                      <small className={styles.fieldHint}>{t('admin.items.forge.costHint' as any)}</small>
                    </label>

                    <label className={styles.formGroup} style={{ gap: 5 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#a09070', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Custo de Energia (⚡)
                      </span>
                      <input
                        type="number"
                        min="0"
                        value={formData.forge_energy_cost ?? 0}
                        onChange={e => setFormData({ ...formData, forge_energy_cost: parseInt(e.target.value) || 0 })}
                        style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(200,160,60,0.25)', color: '#e8dcc8', padding: '8px 10px', borderRadius: 6, fontSize: 13 }}
                      />
                      <small className={styles.fieldHint}>Energia consumida ao forjar.</small>
                    </label>

                    <label className={styles.formGroup} style={{ gap: 5 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#a09070', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {t('admin.items.forge.minXp' as any)}
                      </span>
                      <input
                        type="number"
                        min="0"
                        value={formData.forge_min_xp ?? 0}
                        onChange={e => setFormData({ ...formData, forge_min_xp: parseInt(e.target.value) || 0 })}
                        style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(200,160,60,0.25)', color: '#e8dcc8', padding: '8px 10px', borderRadius: 6, fontSize: 13 }}
                      />
                      <small className={styles.fieldHint}>{t('admin.items.forge.minXpHint' as any)}</small>
                    </label>

                    <label className={styles.formGroup} style={{ gap: 5 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#a09070', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {t('admin.items.forge.time' as any)}
                      </span>
                      <input
                        type="number"
                        min="0"
                        value={formData.forge_time ?? 0}
                        onChange={e => setFormData({ ...formData, forge_time: parseInt(e.target.value) || 0 })}
                        style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(200,160,60,0.25)', color: '#e8dcc8', padding: '8px 10px', borderRadius: 6, fontSize: 13 }}
                      />
                      <small className={styles.fieldHint}>{t('admin.items.forge.timeHint' as any)}</small>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.modalActions}>
              <button className={styles.btnCancel} onClick={() => { setIsCreating(false); setIsEditing(null); }}>{t('common.cancel')}</button>
              <button onClick={handleSave} className={styles.btnSave}>{t('admin.items.btn.save')}</button>
            </div>
          </div>
        </div>
      )}

      {showGuide && (
        <div className={styles.modalOverlay} onClick={() => setShowGuide(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <h3>📖 {t('guide.items.title' as any)}</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <p style={{ color: '#e8dcc8', fontSize: 14, margin: 0, lineHeight: 1.5 }}>
                {t('guide.items.intro' as any)}
              </p>

              <div style={{ background: 'rgba(0,0,0,0.2)', padding: 16, borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
                <h4 style={{ color: '#64b4ff', margin: '0 0 8px 0', fontSize: 15 }}>
                  ⚔️ {t('guide.items.equipmentTitle' as any)}
                </h4>
                <p style={{ color: '#a09070', fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                  {t('guide.items.equipmentDesc' as any)}
                </p>
              </div>

              <div style={{ background: 'rgba(255, 136, 51, 0.05)', padding: 16, borderRadius: 8, border: '1px solid rgba(255, 136, 51, 0.2)' }}>
                <h4 style={{ color: '#ff8833', margin: '0 0 8px 0', fontSize: 15 }}>
                  🔨 {t('guide.items.forgeTitle' as any)}
                </h4>
                <p style={{ color: '#a09070', fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                  {t('guide.items.forgeDesc' as any)}
                </p>
              </div>

              <div style={{ background: 'rgba(68, 204, 136, 0.05)', padding: 16, borderRadius: 8, border: '1px solid rgba(68, 204, 136, 0.2)' }}>
                <h4 style={{ color: '#44cc88', margin: '0 0 8px 0', fontSize: 15 }}>
                  🎁 {t('guide.items.dropsTitle' as any)}
                </h4>
                <p style={{ color: '#a09070', fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                  {t('guide.items.dropsDesc' as any)}
                </p>
              </div>
            </div>

            <div className={styles.modalActions} style={{ justifyContent: 'center', marginTop: 20 }}>
              <button 
                className={styles.btnSave} 
                style={{ minWidth: 200 }}
                onClick={() => setShowGuide(false)}
              >
                {t('guide.attributes.understood' as any)}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer messages={messages} onRemove={remove} />
    </div>
  );
}
